import { html, css, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import { DynamicValue } from '../../shared/alphatab-enums.js';
// Shared with the scoring/judging code — see the bundle note in dynamics-ranges.ts.
import { DYNAMICS_RANGES, normalizeDynamics, dynamicsLabel } from '../../dynamics-ranges.js';

const DECAY_DELAY_MS = 300;
const MAX_VELOCITY = 127;
const YELLOW_ZONE_WIDTH = 20;

const GREEN_COLOR = '#10b981';
const YELLOW_COLOR = '#f59e0b';
const RED_COLOR = '#ef4444';

/**
 * Real-time MIDI velocity display with dynamics indicator from score.
 */
@customElement('velocity-meter')
export class VelocityMeter extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: flex;
                flex-direction: column;
                gap: 2px;
                min-width: 90px;
                padding: 4px 8px;
                background: var(--surface);
                border-radius: 6px;
                border: 1px solid var(--border);
            }

            :host(:not([visible])) {
                display: none;
            }

            .dynamics {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 18px;
            }

            .dynamics-label {
                font-family: 'Times New Roman', Georgia, serif;
                font-style: italic;
                font-size: 14px;
                font-weight: bold;
                color: var(--text-muted);
                transition: color 0.2s ease;
            }

            .dynamics-label.active {
                color: var(--text-primary);
            }

            .bar {
                position: relative;
                height: 12px;
                border-radius: 6px;
                background: var(--surface-light);
                overflow: hidden;
                box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.3);
            }

            .fill {
                position: absolute;
                top: 0;
                left: 0;
                height: 100%;
                width: 0%;
                background: ${unsafeCSS(GREEN_COLOR)};
                border-radius: 6px 0 0 6px;
                transition: width 0.05s ease-out, filter 0.3s ease;
            }

            .fill.decaying {
                transition: width 0.5s ease-out, filter 0.3s ease;
            }

            .fill.inactive {
                filter: grayscale(100%) brightness(0.5);
            }

            .marker {
                position: absolute;
                top: -6px;
                width: 2px;
                height: 24px;
                background: var(--text-primary);
                opacity: 0.8;
                transform: translateX(-50%);
                transition: left 0.3s ease;
            }

            .marker::before {
                content: '\u25BC';
                position: absolute;
                top: -12px;
                left: 50%;
                transform: translateX(-50%);
                font-size: 10px;
                color: var(--text-primary);
            }
        `,
    ];

    @property({ type: Boolean, reflect: true })
    visible = false;

    // Cached shadow DOM refs for performance (updateVelocity is called at MIDI frequency)
    private _fill: HTMLElement | null = null;
    private _label: HTMLElement | null = null;
    private _marker: HTMLElement | null = null;
    private _decayTimeoutId: number | null = null;
    private _currentMarkerVelocity = 73; // Default to mf midpoint

    firstUpdated(): void {
        this._fill = this.renderRoot.querySelector('.fill');
        this._label = this.renderRoot.querySelector('.dynamics-label');
        this._marker = this.renderRoot.querySelector('.marker');
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this._clearDecayTimeout();
    }

    private _velocityToPercent(velocity: number): number {
        return (velocity / MAX_VELOCITY) * 100;
    }

    private _clearDecayTimeout(): void {
        if (this._decayTimeoutId !== null) {
            window.clearTimeout(this._decayTimeoutId);
            this._decayTimeoutId = null;
        }
    }

    private _startDecay(): void {
        if (!this._fill) return;
        this._fill.classList.add('decaying');
        this._fill.style.width = '0%';
        setTimeout(() => {
            if (this._fill) {
                this._fill.classList.add('inactive');
            }
        }, 500);
    }

    /**
     * Update the velocity meter fill position.
     * @param velocity MIDI velocity value (0-127)
     */
    updateVelocity(velocity: number): void {
        if (!this._fill) return;

        this._clearDecayTimeout();

        const clampedVelocity = Math.max(0, Math.min(MAX_VELOCITY, velocity));
        const fillPercent = this._velocityToPercent(clampedVelocity);
        const markerPercent = this._velocityToPercent(this._currentMarkerVelocity);
        const transitionWidth = 3;

        let gradient: string;
        if (clampedVelocity <= this._currentMarkerVelocity) {
            gradient = GREEN_COLOR;
        } else {
            const excessVelocity = clampedVelocity - this._currentMarkerVelocity;
            if (excessVelocity <= YELLOW_ZONE_WIDTH) {
                const transitionEnd = Math.min(markerPercent + transitionWidth, 100);
                gradient = `linear-gradient(to right, ${GREEN_COLOR} 0%, ${GREEN_COLOR} ${markerPercent}%, ${YELLOW_COLOR} ${transitionEnd}%, ${YELLOW_COLOR} 100%)`;
            } else {
                const yellowEndPercent = this._velocityToPercent(this._currentMarkerVelocity + YELLOW_ZONE_WIDTH);
                const greenToYellowEnd = Math.min(markerPercent + transitionWidth, yellowEndPercent);
                const yellowToRedEnd = Math.min(yellowEndPercent + transitionWidth, 100);
                gradient = `linear-gradient(to right, ${GREEN_COLOR} 0%, ${GREEN_COLOR} ${markerPercent}%, ${YELLOW_COLOR} ${greenToYellowEnd}%, ${YELLOW_COLOR} ${yellowEndPercent}%, ${RED_COLOR} ${yellowToRedEnd}%, ${RED_COLOR} 100%)`;
            }
        }

        this._fill.style.background = gradient;
        this._fill.style.width = `${fillPercent}%`;

        if (clampedVelocity > 0) {
            this._fill.classList.remove('inactive');
        }
        this._fill.classList.remove('decaying');

        if (clampedVelocity > 0) {
            this._decayTimeoutId = window.setTimeout(() => {
                this._startDecay();
            }, DECAY_DELAY_MS);
        }
    }

    /**
     * Update the expected dynamics indicator.
     * @param dynamics DynamicValue from alphaTab beat
     */
    updateDynamics(dynamics: DynamicValue): void {
        if (!this._label || !this._marker) return;

        const normalizedDynamics = normalizeDynamics(dynamics);

        // Accent/composite marks (sf, sfz, fp, n, ...) are not loudness levels, so
        // there is no target to show. Blank the label but leave the marker where it
        // is — the prevailing dynamic is still whatever it was.
        if (normalizedDynamics === null) {
            this._label.textContent = '--';
            this._label.classList.remove('active');
            return;
        }

        const range = DYNAMICS_RANGES[normalizedDynamics];
        if (!range) {
            console.warn('[VelocityMeter] Unknown dynamics value:', dynamics);
            return;
        }

        this._label.textContent = range.label;
        this._label.classList.add('active');

        this._currentMarkerVelocity = (range.min + range.max) / 2;
        const markerPercent = this._velocityToPercent(this._currentMarkerVelocity);
        this._marker.style.left = `${markerPercent}%`;
    }

    /**
     * Get the label for a dynamics value.
     */
    getDynamicsLabel(dynamics: DynamicValue): string {
        return dynamicsLabel(dynamics);
    }

    /**
     * Clear the velocity meter to empty/inactive state.
     */
    clearMeter(): void {
        this._clearDecayTimeout();

        if (this._fill) {
            this._fill.classList.remove('decaying');
            this._fill.classList.add('inactive');
            this._fill.style.width = '0%';
        }

        if (this._label) {
            this._label.textContent = '--';
            this._label.classList.remove('active');
        }
    }

    /**
     * Reset the velocity meter to default state.
     */
    resetVelocityMeter(): void {
        this.clearMeter();
    }

    render() {
        return html`
            <div class="dynamics">
                <span class="dynamics-label">--</span>
            </div>
            <div class="bar">
                <div class="fill inactive"></div>
                <div class="marker"></div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'velocity-meter': VelocityMeter;
    }
}
